// 치과 「환자 공개 범위」 — Cloud Functions 호출 모음 (2026-10-14 대표 승인, 플래그 clinicPatientShareUi OFF 출고)
// 계획: docs/착수계획_보철제작기록_2026-10-14.md §2-2·§4-1. 서버 callable 은 dev-server 가 동시 제작 중 —
//   CF 이름·입출력 형태가 최종과 다르면 **이 파일 한 곳만** 고친다. 화면은 CF 이름을 직접 알지 않는다.
// 저장 원천: users/{clinicUid}.patientShare 3키 bool (서버 판정은 각 키 !== false 면 켜짐 — 대표 10-15 기본 켜짐 — functions/lib/clinic-patients.js:1407·1419·1948).
//   Firestore 직접 쓰기 금지(rules 에서 CF 전용 필드로 막음). 키가 없으면 켜짐으로 표시한다(서버와 같은 의미).
// ⚠️ 콘솔에는 오류 코드만 남긴다.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// CF 이름 (가칭 — 최종 이름이 바뀌면 여기만)
const CF_GET = 'getPatientShare';
const CF_SET = 'setPatientShare';

export const SHARE_KEYS = Object.freeze(['showMaterial', 'showLabName', 'notifyDesignDone']);

// 서버 응답 share → 3키 bool. false 만 꺼짐(기본 켜짐, 서버 판정과 동일 — 대표 10-15)
export function normalizeShare(raw) {
    const out = {};
    SHARE_KEYS.forEach(k => { out[k] = raw?.[k] !== false; }); // 대표 10-15: 기본 켜짐(서버와 같음 — false 만 꺼짐)
    return out;
}

// 시각 값(ms 숫자 · ISO 문자열 · Firestore Timestamp · {_seconds}) → Date | null
function toDate(v) {
    if (v == null) return null;
    if (typeof v?.toDate === 'function') return v.toDate();
    if (typeof v === 'object' && (v._seconds != null || v.seconds != null)) return new Date((v._seconds ?? v.seconds) * 1000);
    const d = new Date(v);
    return isNaN(d) ? null : d;
}

// 최근 변경 1줄 { at:Date|null, byText:string } | null
export function normalizeLast(raw, clinicUid) {
    if (!raw) return null;
    const at = toDate(raw.at);
    // 서버 last 에 변경자 정보 없음(바꿀 수 있는 사람 = 대표뿐, functions/lib/care-record-card.js setPatientShare) → 고정 표시
    const by = '대표 계정';
    if (!at && !by) return null;
    return { at, byText: by };
}

// → { share:{3키}, last:{at,byText}|null }
export async function readPatientShare(clinicUid) {
    const res = await call(CF_GET)({});
    const d = res?.data || {};
    if (d.ok === false) {
        const err = new Error('getPatientShare ok:false');
        err.details = { code: d.code || 'not-ok' };
        throw err;
    }
    return { share: normalizeShare(d.share), last: normalizeLast(d.last, clinicUid) };
}

// share = 3키 bool 전체를 보낸다 → { ok }
export async function savePatientShare(share) {
    const payload = normalizeShare(share);
    const res = await call(CF_SET)(payload);
    const d = res?.data || {};
    if (d.ok === false) {
        const err = new Error('setPatientShare ok:false');
        err.details = { code: d.code || 'not-ok' };
        throw err;
    }
    return d;
}

export const PATIENT_SHARE_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 오류 → 사용자 문구 (code 는 e.details.code 우선 — 서버 관례 failed-precondition {code:'feature-off'})
export function describePatientShareError(e, fallback = PATIENT_SHARE_GENERIC_ERROR) {
    console.error('[patientShareApi]', e?.code || '', e?.details?.code || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code || '';
    if (c === 'feature-off') return '환자 공개 범위 기능이 아직 열리지 않았습니다.';
    if (c === 'owner-only') return '치과 대표 계정만 바꿀 수 있습니다.';
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    // 코드 없는 permission-denied = 치과가 아니거나 승인 전(서버 resolveClinicActor) — 대표 전용(owner-only)과 구분
    if (e?.code === 'functions/permission-denied') return '승인된 치과 계정에서만 쓸 수 있습니다.';
    if (e?.code === 'functions/not-found') return '이 설정 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/invalid-argument') return '요청 값이 올바르지 않습니다. 화면을 새로고침해 주세요.';
    if (e?.code === 'functions/failed-precondition') return '지금은 바꿀 수 없는 상태입니다. 화면을 새로고침해 주세요.';
    return fallback;
}
